import { dueState, type DueState } from '@kanban/shared';
import { format, isToday, isTomorrow, isYesterday } from 'date-fns';
import { de } from 'date-fns/locale';

/** Datumsformat TT.MM.JJJJ, Zeitzone des Geraets = Europe/Berlin (SPEC 16). */
export function formatDate(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return format(date, 'dd.MM.yyyy', { locale: de });
}

export function formatDateTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return format(date, 'dd.MM.yyyy, HH:mm', { locale: de });
}

/** Nur die Uhrzeit — fuer Angaben wie „wieder möglich ab 16:40". */
export function formatTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return format(date, 'HH:mm', { locale: de });
}

/** Kurzform fuer den Faelligkeits-Chip: "heute", "morgen", sonst TT.MM. */
export function formatDueLabel(value: string | Date, withTime = false): string {
  const date = typeof value === 'string' ? new Date(value) : value;

  const day = isToday(date)
    ? 'heute'
    : isTomorrow(date)
      ? 'morgen'
      : isYesterday(date)
        ? 'gestern'
        : format(date, 'dd.MM.', { locale: de });

  // Die Uhrzeit steht nur dort, wo sie auch gemeint war. Bei einer
  // ganztaegigen Karte waere "23:59" eine Angabe, die niemand gemacht hat.
  return withTime ? `${day} ${format(date, 'HH:mm', { locale: de })}` : day;
}

/** Farbklasse des Faelligkeits-Chips (SPEC 9.1). */
export const DUE_CHIP_CLASS: Record<DueState, string> = {
  none: 'bg-surface-muted text-muted',
  overdue: 'bg-danger-soft text-danger',
  today: 'bg-warning-soft text-warning',
  tomorrow: 'bg-caution-soft text-caution',
  later: 'bg-surface-muted text-muted',
  done: 'bg-success-soft text-success line-through',
};

export { dueState };

/**
 * Vorlaufzeit einer Erinnerung in Worten (SPEC 10.1).
 *
 * Aus der Zahl gerechnet statt aus einer Tabelle gelesen: es gibt zwei
 * Stellen, die sie brauchen — die Auswahl und die Liste der gesetzten
 * Erinnerungen. Zwei Tabellen wuerden auseinanderlaufen, und die Liste fiele
 * bei jedem Wert, den die Auswahl nicht kennt, auf „105 Minuten vorher"
 * zurueck.
 */
export function offsetLabel(minutes: number): string {
  if (minutes <= 0) return 'zum Termin';
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? '1 Tag vorher' : `${days} Tage vorher`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 Stunde vorher' : `${hours} Stunden vorher`;
  }
  // Krumme Werte bleiben in Minuten — „1,5 Stunden vorher" laese sich
  // schlechter als „90 Minuten vorher".
  return `${minutes} Minuten vorher`;
}

/** Initialen fuer den Avatar: maximal zwei Buchstaben. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || '?';
}

/** Zeitangabe im Aktivitaetsverlauf, z. B. "vor 2 Std." */
export function relativeTime(value: string | Date, now: Date = new Date()): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const minutes = Math.round((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.round(hours / 24);
  if (days < 7) return `vor ${days} Tg.`;
  return formatDate(date);
}
